import { createContext, useContext } from 'react';

import type { MenuWidth } from '../menu-constants.ts';
import type { MenuItems } from '../types.ts';

/**
 * A single page in the menu navigation stack.
 *
 * A page is either structured (`items` — rendered by NestedPopup's standard
 * rendering) or free-form (`content` — render-prop returning arbitrary React).
 * The two are mutually exclusive at the type level.
 */
export type MenuPage = {
  title?: string;
  width?: MenuWidth;
  headerAction?: React.ReactNode;
} & (
  | { items: MenuItems; content?: never }
  | { content: (nav: MenuNav) => React.ReactNode; items?: never }
);

/**
 * Navigation API available to any content rendered inside a `<ContextualMenu>`.
 *
 * Exposed both as a render-prop parameter (`content: (nav) => ...`) and via
 * the `useMenuNav()` hook for deeply nested descendants.
 */
export interface MenuNav {
  /** Close the entire menu popup. */
  close: () => void;
  /** Pop the current drilldown page (no-op at depth 0). */
  pop: () => void;
  /** Push a new drilldown page onto the stack. */
  push: (page: MenuPage) => void;
  /** Replace the current page without growing the stack. */
  replace: (page: MenuPage) => void;
  /** Current stack depth: 0 at root, +1 per drilldown push. */
  depth: number;
}

/**
 * React context for the menu navigation API.
 * Provided by ContextualMenu / NestedPopup; consumed via `useMenuNav()`.
 */
export const MenuNavContext = createContext<MenuNav | null>(null);
MenuNavContext.displayName = 'MenuNavContext';

/**
 * Access the `MenuNav` navigation API from inside any menu descendant.
 *
 * Throws if used outside a `<ContextualMenu>`.
 */
export const useMenuNav = (): MenuNav => {
  const nav = useContext(MenuNavContext);
  if (!nav) {
    throw new Error(
      'useMenuNav() must be used inside a <ContextualMenu>. ' +
        'Ensure the component is rendered as a descendant of a menu.'
    );
  }
  return nav;
};
